import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { BRAND, NAV_LINKS } from "../lib/brand";

// Render a smart nav link: react-router Link for plain paths, anchor for hashes.
const NavItem = ({ link, onClick, active, mobile = false }) => {
  // Nav links are ALWAYS gold (visible on both dark hero and cream inner pages)
  const baseColor = "text-[#D4A24C] hover:text-[#C29344]";
  const activeColor = "text-[#C29344]";

  const className = mobile
    ? `text-base font-medium ${active ? "text-[#C29344]" : "text-[#1F4A38]"}`
    : `relative text-sm font-semibold tracking-wide transition-colors duration-300 ${
        active ? activeColor : baseColor
      } after:content-[''] after:absolute after:left-0 after:-bottom-1.5 after:h-[1.5px] ${
        active ? "after:w-full after:bg-[#D4A24C]" : "after:w-0 after:bg-[#D4A24C]"
      } after:transition-all after:duration-300 hover:after:w-full`;

  // Hash links (e.g. "/#about") — plain anchor so browser scrolls
  if (link.href.startsWith("#") || link.href.includes("#")) {
    return (
      <a
        href={link.href}
        onClick={onClick}
        data-testid={`${mobile ? "mobile-" : ""}nav-link-${link.label
          .toLowerCase()
          .replace(/\s+/g, "-")}`}
        className={className}
      >
        {link.label}
      </a>
    );
  }
  return (
    <Link
      to={link.href}
      onClick={onClick}
      data-testid={`${mobile ? "mobile-" : ""}nav-link-${link.label
        .toLowerCase()
        .replace(/\s+/g, "-")}`}
      className={className}
    >
      {link.label}
    </Link>
  );
};

export const Header = ({ onBook }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const isActive = (href) => {
    if (href === "/") return location.pathname === "/";
    if (href.startsWith("/") && !href.includes("#"))
      return location.pathname === href;
    return false;
  };

  return (
    <header
      data-testid="header-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[#F9F6F0]/85 backdrop-blur-xl border-b border-[#1F4A38]/10 shadow-[0_4px_24px_rgba(31,74,56,0.06)]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="flex items-center group">
          <span
            className={`flex items-center justify-center transition-all duration-500 rounded-2xl ${
              scrolled
                ? "bg-transparent p-0"
                : "bg-[#F9F6F0]/90 backdrop-blur-md px-3 py-1.5 shadow-[0_6px_20px_rgba(0,0,0,0.18)]"
            }`}
          >
            <img
              src={BRAND.logoUrl}
              alt="Yogiinit"
              className="h-12 md:h-14 w-auto object-contain transition-transform duration-500 group-hover:scale-105"
              style={{ background: "transparent" }}
            />
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          {NAV_LINKS.map((l) => (
            <NavItem key={l.label} link={l} active={isActive(l.href)} />
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="book-now-btn"
            onClick={onBook}
            className="hidden sm:inline-flex items-center gap-2 bg-[#D4A24C] hover:bg-[#C29344] text-[#153326] rounded-full px-6 py-2.5 text-sm font-semibold tracking-wide transition-all duration-300 shadow-[0_8px_24px_rgba(212,162,76,0.35)] hover:shadow-[0_12px_28px_rgba(212,162,76,0.5)] hover:-translate-y-0.5"
          >
            Free Trial
          </button>
          <button
            data-testid="mobile-menu-btn"
            onClick={() => setOpen((o) => !o)}
            className={`lg:hidden p-2 rounded-full transition ${
              scrolled ? "text-[#1F4A38]" : "text-white"
            }`}
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <div
        className={`lg:hidden overflow-hidden transition-[max-height,opacity] duration-500 ${
          open ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
        } bg-[#F9F6F0]/95 backdrop-blur-xl border-t border-[#1F4A38]/10`}
      >
        <div className="px-6 py-6 flex flex-col gap-4">
          {NAV_LINKS.map((l) => (
            <NavItem
              key={l.label}
              link={l}
              scrolled
              mobile
              active={isActive(l.href)}
              onClick={() => setOpen(false)}
            />
          ))}
          <button
            data-testid="mobile-book-btn"
            onClick={() => {
              setOpen(false);
              onBook?.();
            }}
            className="mt-2 bg-[#D4A24C] hover:bg-[#C29344] text-[#153326] rounded-full px-6 py-3 text-sm font-semibold self-start"
          >
            Free Trial
          </button>
        </div>
      </div>
    </header>
  );
};

export default Header;
